<template>
    <div>
        <div id="Dashboard" class="container is-fluid">
            <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />

            <!-- Header -->
            <HeroContainer title="Parts Dashboard" />

            <section id="filters" class="filters-section">
                <div class="card shadow-sm filters-card">
                    <div class="card-body filters-body">
                        <div class="field is-horizontal">
                            <div class="field-label is-normal">
                                <label class="label">Customer</label>
                            </div>
                            <div class="field-body">
                                <div class="field">
                                    <div class="select" style="width: 100%">
                                        <select
                                            id="search_customer"
                                            style="width: 100%"
                                            v-model="customer"
                                        >
                                            <option value="">All Customers</option>
                                            <option
                                                v-for="item in customer_items"
                                                :key="'cust_' + item"
                                                :value="item"
                                            >
                                                {{ item }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="field is-horizontal">
                            <div class="field-label is-normal">
                                <label class="label">Vendor</label>
                            </div>
                            <div class="field-body">
                                <div class="field">
                                    <span class="select">
                                        <select
                                            id="q_vendor_select"
                                            style="width: 100%"
                                            v-model="vendor"
                                        >
                                            <option value="">All Vendors</option>
                                            <option
                                                v-for="item in vendor_items"
                                                :key="'vend_' + item"
                                                :value="item"
                                            >
                                                {{ item }}
                                            </option>
                                        </select>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div class="field is-horizontal">
                            <div class="field-label is-normal">
                                <label class="label">Period</label>
                            </div>
                            <div class="field-body">
                                <div class="field has-addons">
                                    <p class="control">
                                        <span class="select">
                                            <select
                                                id="date_range"
                                                v-model="date_range"
                                                @change="change_date(date_range)"
                                            >
                                                <option
                                                    disabled
                                                    hidden
                                                    value=""
                                                    style="color: grey"
                                                >
                                                    Date Presets
                                                </option>
                                                <option
                                                    v-for="(selectOption, index) in selectOptions"
                                                    :value="selectOption.value"
                                                    :key="index"
                                                >
                                                    {{ selectOption.label }}
                                                </option>
                                                <option disabled hidden value="custom">
                                                    Custom
                                                </option>
                                            </select>
                                        </span>
                                    </p>
                                    <p class="control is-expanded">
                                        <VueDatePicker
                                            v-model="range"
                                            :enable-time-picker="false"
                                            range
                                        />
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="buttons is-right filters-actions">
                            <button @click="reset" class="button is-small is-white">Reset</button>
                            <div class="filter-action">
                                <button
                                    @click="ajax_get_dashboard"
                                    class="button is-info"
                                    :disabled="isSearching"
                                >
                                    Filter
                                </button>
                                <span
                                    v-if="isSearching"
                                    class="loading-icon"
                                    aria-live="polite"
                                    aria-label="Loading parts"
                                >
                                    <i class="material-icons rotating">.</i>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section id="dashboard_part_sales" v-if="queryData.hasOwnProperty('part_sales')">
                <div class="title_div">
                    <span class="site">Parts Sold</span>
                </div>
                <PartsDashboardTable
                    ref="dash_part_sales"
                    :rows="queryData.part_sales.data"
                    :columns="PartsSalesColumnsHeader"
                    :enable-pagination="is_paged_sales"
                >
                    <template #selected-row-actions>
                        <button class="btn btn-link p-0" @click="check_stock('dash_part_sales')">
                            Check Textron/Defense Stock
                        </button>
                    </template>
                </PartsDashboardTable>
                <div class="buttons is-right">
                    <button
                        class="button is-info"
                        style="margin: 0.5em 0 0 0"
                        @click="
                            $emit('download-csv', [
                                queryDataRaw.part_sales.data,
                                'PartSalesDashboard',
                                null,
                            ])
                        "
                    >
                        Download CSV
                    </button>
                </div>
            </section>

            <section id="dashboard_part_quotes" v-if="queryData.hasOwnProperty('part_quotes')">
                <div class="title_div">
                    <span class="site">Parts Quoted</span>
                </div>
                <PartsDashboardTable
                    ref="dash_part_quotes"
                    :rows="queryData.part_quotes.data"
                    :columns="PartsQuotesColumnsHeader"
                    :enable-pagination="is_paged_quotes"
                >
                    <template #selected-row-actions>
                        <button class="btn btn-link p-0" @click="check_stock('dash_part_quotes')">
                            Check Textron/Defense Stock
                        </button>
                    </template>
                </PartsDashboardTable>
                <div class="buttons is-right">
                    <button
                        class="button is-info"
                        style="margin: 0.5em 0 0 0"
                        @click="
                            $emit('download-csv', [
                                queryDataRaw.part_quotes.data,
                                'PartQuotesDashboard',
                                null,
                            ])
                        "
                    >
                        Download CSV
                    </button>
                </div>
            </section>
        </div>

        <!-- Search Results Modal -->
        <div v-if="showSearchModal" class="modal-backdrop" @click.self="closeSearchModal">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Search Results</h5>
                    <button
                        type="button"
                        class="btn-close"
                        aria-label="Close"
                        @click="closeSearchModal"
                    ></button>
                </div>
                <div class="modal-body">
                    <div v-if="searchTables.length === 0" class="text-center py-4 text-muted">
                        No search results to display.
                    </div>
                    <div v-else>
                        <section v-for="table in searchTables" :key="table.key" class="mb-3">
                            <div class="title_div mb-2">
                                <span class="site">{{ table.title }}</span>
                            </div>
                            <PartsDashboardTable
                                :rows="table.rows"
                                :columns="table.columns"
                                :enable-pagination="table.rows.length > 10"
                                :row-key-field="table.rowKeyField"
                            />
                        </section>
                    </div>
                </div>
                <div class="modal-footer d-flex justify-content-end">
                    <button type="button" class="btn btn-secondary" @click="closeSearchModal">
                        Close
                    </button>
                </div>
            </div>
        </div>

        <div v-if="searchTables.length > 0 && !showSearchModal" class="floating-results-button">
            <button class="btn btn-info" @click="openSearchModal">View Search Results</button>
        </div>
    </div>
</template>

<style scoped>
    .title_div {
        margin-top: 1.5em;
    }

    .site {
        font-size: 2em;
    }

    .filters-section {
        display: flex;
        justify-content: center;
    }

    .filters-card {
        width: 100%;
        max-width: 720px;
        margin: 0 auto 1.5em;
    }

    .filters-body {
        display: flex;
        flex-direction: column;
        gap: 0.75em;
    }

    .filters-actions {
        margin-top: 0.5em;
        gap: 0.75em;
    }

    .filter-action {
        display: flex;
        align-items: center;
        gap: 0.5em;
    }

    .loading-icon {
        display: flex;
        align-items: center;
        color: #3273dc;
    }

    .rotating {
        animation: spin 1s linear infinite;
    }

    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }

    .modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(15, 23, 42, 0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1050;
        padding: 1rem;
    }

    .modal-content {
        background: #fff;
        border-radius: 8px;
        width: min(1200px, 95vw);
        max-height: 90vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    }

    .modal-header,
    .modal-footer {
        padding: 12px 16px;
        border-bottom: 1px solid #e5e7eb;
    }

    .modal-footer {
        border-top: 1px solid #e5e7eb;
        border-bottom: none;
    }

    .modal-title {
        margin: 0;
        font-size: 1.1rem;
        font-weight: 600;
    }

    .modal-body {
        padding: 16px;
        overflow: auto;
    }

    .btn-close {
        background: none;
        border: none;
        font-size: 1.25rem;
        line-height: 1;
    }

    .floating-results-button {
        position: fixed;
        bottom: 16px;
        right: 16px;
        z-index: 1040;
    }
</style>

<script>
    import moment from 'moment'
    import VueDatePicker from '@vuepic/vue-datepicker'
    import { getDates } from '@/helpers/dates.js'
    import HeroContainer from '@/components/shared/HeroContainer.vue'
    import PartsQuotesColumnsHeader from '@/data/PartsQuotesColumnsHeader.js'
    import PartsSalesColumnsHeader from '@/data/PartsSalesColumnsHeader.js'
    import PartsDashboardTable from '@/components/shared/PartsDashboardTable.vue'
    import { mapStores, mapWritableState } from 'pinia'
    import { useDropdownsStore } from '@/stores/dropdownsStore'
    import { useDashboardPartsStore } from '@/stores/dashboardPartsStore'
    import { toast } from 'vue3-toastify'

    export default {
        name: 'DashboardParts',
        components: {
            VueDatePicker,
            HeroContainer,
            PartsDashboardTable,
        },
        computed: {
            ...mapStores(useDropdownsStore, useDashboardPartsStore),
            ...mapWritableState(useDashboardPartsStore, {
                figureKey: 'figureKey',
                customer: 'customer',
                vendor: 'vendor',
                date_range: 'date_range',
                range: 'range',
                queryDataRaw: 'queryDataRaw',
                stock_data: 'stock_data',
                searchDataRaw: 'searchDataRaw',
                response_buffer: 'response_buffer',
            }),
            is_sales() {
                if (Object.prototype.hasOwnProperty.call(this.queryData, 'part_quotes')) {
                    let q = this.queryData.part_quotes
                    return q.data.lenth > 0
                }
                return false
            },
            is_quotes() {
                if (Object.prototype.hasOwnProperty.call(this.queryData, 'part_sales')) {
                    let s = this.queryData.part_sales
                    return s.data.lenth > 0
                }
                return false
            },
            is_paged_sales: function () {
                const rows = this.queryData?.part_sales?.data || []
                return rows.length > 10
            },
            is_paged_quotes: function () {
                const rows = this.queryData?.part_quotes?.data || []
                return rows.length > 10
            },
            customer_items() {
                // For customer dropdown
                return this.dropdownsStore.customer
            },
            vendor_items() {
                // For vendor dropdown
                return this.dropdownsStore.vendor
            },
            queryData() {
                let raw_query = { ...this.queryDataRaw }
                return raw_query
            },
            isSearching() {
                return this.searchStore.ajax_searching > 0
            },
            searchData: function () {
                // Clean the raw search data and return data
                // containing a single datateble for every
                // site-part pair
                let raw_data = [...this.searchDataRaw]
                let cleaned_search_data = []
                let quote_sale = ['Quotes', 'Sales']
                let inventory = [
                    'Greenwood Ponca Inventory',
                    'GlobalParts Inventory',
                    'Inventory ComponentControl',
                    'Inventory GlobalParts',
                ]
                var research_subs_dict = {}
                // Loop through raw data
                for (let raw_idx in raw_data) {
                    let raw_site = raw_data[raw_idx].site
                    let raw_part = raw_data[raw_idx].part
                    let raw_sub = raw_data[raw_idx].substitute_for
                    // Part research could be on substitutes, so keep track of the reverse relations
                    if (raw_sub) {
                        if (!Object.prototype.hasOwnProperty.call(research_subs_dict, raw_sub)) {
                            research_subs_dict[raw_sub] = []
                        }
                        if (!research_subs_dict[raw_sub].includes(raw_part)) {
                            research_subs_dict[raw_sub].push(raw_part)
                        }
                    }
                    let idx_match = -1
                    let idx_overwrite = -1
                    // Compare raw data to cleaned data to see if raw should be appended to clean
                    for (let clean_idx in cleaned_search_data) {
                        let clean_site = cleaned_search_data[clean_idx].site
                        let clean_part = cleaned_search_data[clean_idx].part

                        // Handle inventory data
                        if (
                            (clean_site == raw_site) &
                            inventory.includes(clean_site) &
                            (clean_part == raw_part)
                        ) {
                            idx_overwrite = clean_idx
                            break
                        }
                        // Handle quote/sales data
                        if (quote_sale.includes(clean_site)) {
                            if ((clean_site == raw_site) & (clean_part == raw_part)) {
                                idx_overwrite = clean_idx
                                break
                            }
                        }
                        // Handle search data
                        if (
                            (clean_site == raw_site) &
                            ((clean_part == raw_sub) | (clean_part == raw_part))
                        ) {
                            idx_match = clean_idx
                            break
                        }
                    }
                    if (idx_overwrite > -1) {
                        let overwrite_data = JSON.parse(JSON.stringify(raw_data[raw_idx]))
                        cleaned_search_data[idx_overwrite] = overwrite_data
                    } else if (idx_match < 0) {
                        // Create a new datatable (deep clone the original object)
                        let copy_data = JSON.parse(JSON.stringify(raw_data[raw_idx]))
                        cleaned_search_data.push(copy_data)
                    } else {
                        // Append to the existing data
                        let append_data = [...raw_data[raw_idx].data]
                        cleaned_search_data[idx_match].data.push(...append_data)
                        // Remove any Data=None rows
                    }
                }
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.research_subs_dict = research_subs_dict
                return cleaned_search_data
            },
            searchTables() {
                return this.searchData.map((sd, idx) => {
                    const rows = Array.isArray(sd?.data) ? sd.data : []
                    const columns = this.buildSearchColumns(rows)
                    const rowKeyField = this.getSearchRowKey(columns)
                    const titleParts = [sd.site || 'Search Results']
                    if (sd.part) titleParts.push(`- ${sd.part}`)
                    return {
                        key: `${sd.site || 'search'}-${sd.part || sd.substitute_for || idx}`,
                        title: titleParts.join(' '),
                        rows,
                        columns,
                        rowKeyField,
                    }
                })
            },
        },
        data: function () {
            return {
                numeral: require('numeral'),
                selectOptions: [
                    { value: 'today', label: 'Today' },
                    { value: 'week', label: 'Week' },
                    { value: 'this_month', label: 'This Month' },
                    { value: 'prev_month', label: 'Prev Month' },
                    { value: 'this_quarter', label: 'This Quarter' },
                    { value: 'prev_quarter', label: 'Prev Quarter' },
                    { value: 'this_year', label: 'This Year' },
                    { value: 'prev_year', label: 'Prev Year' },
                    { value: 'all', label: 'All' },
                ],
                PartsQuotesColumnsHeader,
                PartsSalesColumnsHeader,
                showSearchModal: false,
            }
        },
        watch: {
            plotData: function () {
                // Change key to force the component to re-render
                // otherwise it will have height=0
                this.dashboardPartsStore.addToFigureKeyParts(1)
            },
        },
        methods: {
            getDates,
            openSearchModal() {
                this.showSearchModal = true
            },
            closeSearchModal() {
                this.showSearchModal = false
            },
            toTitleCase(input) {
                return input
                    .split('_')
                    .map((i) => (i ? i[0].toUpperCase() + i.substring(1) : ''))
                    .join(' ')
            },
            buildSearchColumns(rows = []) {
                const keys = new Set()
                rows.forEach((row) => {
                    Object.keys(row || {}).forEach((k) => keys.add(k))
                })
                if (!keys.size) return []

                return Array.from(keys).map((key) => {
                    const sampleRow = rows.find(
                        (row) => row && row[key] !== undefined && row[key] !== null
                    )
                    const sampleValue = sampleRow ? sampleRow[key] : null
                    const numeric = this.isNumericValue(sampleValue)
                    return {
                        label: this.toTitleCase(key),
                        field: key,
                        type: numeric ? 'number' : undefined,
                    }
                })
            },
            isNumericValue(value) {
                if (value === null || value === undefined) return false
                const cleaned = String(value).replace(/[^0-9.-]+/g, '')
                if (!cleaned.length) return false
                return !Number.isNaN(Number(cleaned))
            },
            getSearchRowKey(columns = []) {
                const preferred = ['part_number', 'part', 'Part_Number', 'part_num', 'partnumber']
                const availableFields = columns.map((col) => col.field)
                const found = preferred.find((field) => availableFields.includes(field))
                return found || availableFields[0] || 'part_number'
            },
            showToast(message, type = 'default') {
                toast(message, {
                    type,
                    pauseOnFocusLoss: false,
                })
            },

            check_stock(table_ref) {
                const table = this.$refs[table_ref]
                const part_rows = table?.selectedRows || []
                const parts_list = part_rows.map((row) => row.part_number).filter(Boolean)
                console.log('Checking stock for parts:', parts_list)
                if (parts_list.length > 0) {
                    this.search_stream(parts_list)
                }
            },
            plot_click(click_data) {
                let date = click_data.points[0].x
                let period = this.period_last_filtered

                let dt = moment.utc(date)
                let dt2 = moment.utc(dt).endOf(period)
                this.ajax_get_detail(dt.toISOString(), dt2.toISOString())
            },
            reset() {
                this.dashboardPartsStore.resetDashboardParts()
            },
            change_date(date_range) {
                let range = this.getDates(date_range)
                if (range) {
                    this.range = range
                }
            },
            set_searching(is_searching) {
                this.searchStore.set_ajax_searching(is_searching)
            },
            filter_records() {
                this.ajax_get_dashboard(this.filter_customer, this.filter_user)
            },
            ajax_get_dashboard() {
                this.queryDataRaw = {}
                this.period_last_filtered = this.period
                let begin_date = moment.utc(this.range[0]).format('YYYY-MM-DD')
                let end_date = moment.utc(this.range[1]).endOf('day').format('YYYY-MM-DD')
                this.set_searching(true)
                this.dashboardPartsStore
                    .fetchDashboardParts({
                        begin_date,
                        end_date,
                        customer: this.customer,
                        vendor: this.vendor,
                    })
                    .catch((e) => {
                        console.error(JSON.stringify(e?.message || e))
                    })
                    .finally(() => {
                        this.set_searching(false)
                    })
            },
            ajax_get_detail(begin_date, end_date) {
                this.set_searching(true)
                this.dashboardPartsStore
                    .fetchDashboardDetail({ begin_date, end_date })
                    .catch((e) => {
                        console.error(JSON.stringify(e?.message || e))
                    })
                    .finally(() => {
                        this.set_searching(false)
                    })
            },
            end_search() {
                console.log('Search complete')
                this.set_searching(false)
                this.showToast('Search complete')
                if (this.searchData.length > 0) {
                    this.openSearchModal()
                } else {
                    this.showToast('No stock data available', 'info')
                }
            },
            search_stream(part_number_list) {
                this.searchDataRaw = []
                this.response_buffer = ''
                this.set_searching(true)
                this.showToast('Searching stock')
                this.dashboardPartsStore
                    .streamStock(part_number_list)
                    .then((reader) => {
                        this.writeToDOM(reader)
                    })
                    .catch((e) => {
                        console.error(JSON.stringify(e?.message || e))
                        this.set_searching(false)
                    })
            },
            writeToDOM(reader) {
                reader.read().then(
                    ({ value, done }) => {
                        if (done) {
                            if (this.response_buffer && this.response_buffer.trim().length) {
                                this.processBufferedResponse(this.response_buffer, true)
                                this.response_buffer = ''
                            }
                            this.end_search()
                        } else {
                            let d = new TextDecoder()
                            let val = d.decode(value)
                            if (val.includes('{{ENDREC}}')) {
                                const finished_buffer = this.response_buffer + val
                                this.response_buffer = ''
                                const split = finished_buffer.split('{{ENDREC}}')
                                split.forEach((record) => this.processBufferedResponse(record))
                            } else {
                                this.response_buffer = this.response_buffer + val
                            }
                            // Recursively call
                            this.writeToDOM(reader)
                        }
                    },
                    (e) => console.error('The stream became errored and cannot be read from!', e)
                )
            },
            processBufferedResponse(record, isFinal = false) {
                const trimmed = (record || '').trim()
                if (!trimmed) {
                    return
                }

                try {
                    const parsed = JSON.parse(trimmed)

                    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.data)) {
                        const entry = {
                            site: 'Stock Search',
                            part: '',
                            substitute_for: '',
                            data: parsed.data,
                        }
                        this.searchDataRaw = this.searchDataRaw.concat(entry)
                        if (parsed.data.length === 0) {
                            this.showToast('No stock data available', 'info')
                        }
                        return
                    }

                    if (Array.isArray(parsed)) {
                        this.searchDataRaw = this.searchDataRaw.concat(parsed)
                    }
                } catch (error) {
                    console.error('Failed to parse search data buffer', error)
                    if (isFinal) {
                        this.showToast('No stock data available', 'info')
                    }
                }
            },
        },
    }
</script>
